/**
 * Loading Paddle.js, on demand.
 *
 * Deliberately not a module import and not a `<script>` in `index.html`.
 * Paddle's script is fetched only when someone opens the billing page, so the
 * rest of the workspace — which is every page an analyst actually spends time
 * on — never pays for it, and a deployment with no Paddle configured never
 * loads it at all.
 *
 * The environment is not configured here either. `Paddle.Environment.set` is
 * only needed for the sandbox, and which one this deployment talks to is the
 * backend's business — so the backend says so, in the config the page fetches
 * alongside its plans. Nothing about Paddle is baked into the bundle, which is
 * what keeps one build correct in every environment.
 */

const SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";

interface PaddleCheckout {
  open(options: { transactionId: string }): void;
}

interface PaddleGlobal {
  Environment?: { set(environment: string): void };
  Initialize(options: { token: string }): void;
  Checkout: PaddleCheckout;
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

let loading: Promise<PaddleGlobal> | null = null;

/** Load Paddle.js once per page, whoever asks and however often. */
export function loadPaddle(): Promise<PaddleGlobal> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (loading) return loading;

  loading = new Promise<PaddleGlobal>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.Paddle) {
        resolve(window.Paddle);
        return;
      }
      // Loaded but did not define its global: a proxy served something else,
      // or an extension blocked it. Either way there is nothing to call.
      reject(new Error("Paddle.js loaded without defining Paddle"));
    };
    script.onerror = () => {
      // The usual cause is an ad blocker, and it is worth saying so: the
      // alternative is a button that does nothing for no visible reason.
      loading = null;
      reject(new Error("Paddle.js could not be loaded"));
    };
    document.head.appendChild(script);
  });

  return loading;
}

/** Reset the module-level cache. Test seam; nothing in the app calls it. */
export function resetPaddleForTests(): void {
  loading = null;
  delete window.Paddle;
}
